<template>
    <!-- Playbook Copy Modal -->
    <div class="modal fade" id="playbook-copy-modal" tabindex="-1" aria-labelledby="playbook-copy-modal-label" aria-hidden="true" data-bs-backdrop="static">
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content playbook-creation-modal">
          <div class="modal-header border-0 pb-0"> 
            <h2 class="modal-title fw-bold text-dark mb-2 mx-2">Copy Playbook</h2>
            <button type="button" class="btn btn-white float-end close-modal" data-bs-dismiss="modal" @click.prevent="closeModal()" data-bs-toggle="tooltip" title="Close"> 
              <img class="icon" src="/img/icons/avid-icons/close.svg" alt=""> 
            </button>
          </div>
  
          <div class="modal-body px-4 pb-0">
            <p class="text-muted mb-4">Copy "{{ originalPlaybook?.name }}" and customize the details below.</p>
       
            <input type="text" id="play_name" v-model="local_playbook.name" 
                class="form-control mb-2" :class="{ 'is-invalid': errors.name }"
                placeholder="Name your play" aria-label="Name your play">
            <div v-if="errors.name" class="invalid-feedback d-block mb-2">{{ errors.name }}</div>
 
            <textarea id="play_description" v-model="local_playbook.description" 
                class="form-control mb-3" :class="{ 'is-invalid': errors.description }"
                placeholder="Describe your play (optional)"
                rows="3"></textarea>
            <div v-if="errors.description" class="invalid-feedback d-block mb-2">{{ errors.description }}</div>

            <div class="row g-3 mb-4 align-items-md-center">
                <div class="col-12 col-lg-auto">
                    <nav class="navbar navbar-expand tertiary-tab-bar flex-md-shrink-0 w-100">
                        <ul class="navbar-nav">
                            <li class="nav-item" style="pointer-events: none;">
                                <span class="nav-link d-flex flex-nowrap tertiary-tab" style="cursor: default !important; pointer-events: none;"><span>Base On</span></span>
                            </li>
                            <li class="nav-item">
                                <a
                                    class="nav-link d-flex flex-nowrap tertiary-tab"
                                    :class="{ 'tertiary-tab-active': date_type === 'start' }"
                                    href="#"
                                    @click.prevent="date_type = 'start'"
                                ><span>Start Date</span></a>
                            </li>
                            <li class="nav-item">
                                <a
                                    class="nav-link d-flex flex-nowrap tertiary-tab"
                                    :class="{ 'tertiary-tab-active': date_type === 'end' }"
                                    href="#"
                                    @click.prevent="date_type = 'end'"
                                ><span>End Date</span></a>
                            </li>
                        </ul>
                    </nav>
                </div>

                <div class="col-12 col-lg">
                    <datepicker-utc 
                        v-model="local_playbook.date" 
                        input-class="form-control flex-grow-1 w-100"
                        placeholder="Select Date"
                    ></datepicker-utc>
                </div>
            </div>
          </div>
          
          <div class="modal-footer px-4 pb-4">
            <button v-if="canEdit('playbooks')" type="button" class="btn btn-primary rounded-pill px-4 py-2" @click="copyPlaybook()" :disabled="!local_playbook.name || !local_playbook.date">
              Copy Playbook
            </button>
            <div v-else data-bs-toggle="tooltip" title="You do not have privileges to complete this action">
              <button type="button" class="btn btn-primary rounded-pill px-4 py-2" :disabled="true">
                Copy Playbook
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import { defineComponent } from 'vue'
  import permissionsMixin from '../../mixins/permissions';
  import { usePlaybookStore } from '../../stores/playbook';
  import { useClientStore } from '../../stores/client';
  
  export default defineComponent({
    name: 'PlaybookCopyModal',
    mixins: [permissionsMixin],
    emits: ['update:show','play-creating','play-created'],
    props: {
      show: {
        type: Boolean,
        default: false
      },
      originalPlaybook: {
        type: Object,
        default: null
      }
    },
  data() {
    return {
      local_playbook: {
        name: '',
        description: '',
        date: null,
      },
      date_type: 'start',
      // Main modal
      copy_modal: null,
      modal_initialized: false,
      // Error handling
      errors: {
        name: '',
        description: '',
        date: '',
      }
    }
  },
    setup() {
      const playbookStore = usePlaybookStore();
      const clientStore = useClientStore();
      return {
        playbookStore,
        clientStore
      };
    },
    computed: {
      client() {
        return this.clientStore.getClient;
      }
    },
    beforeMount() {
      if (this.originalPlaybook) {
        this.local_playbook.name = this.originalPlaybook.name + ' (Copy)';
        this.local_playbook.description = this.originalPlaybook.description || '';
        this.local_playbook.date = this.originalPlaybook.start_date || null;
      }
    },
    watch: {
      show: {
        handler(newVal) {
          if (newVal) {
            this.$nextTick(() => {
              // Initialize the copy modal when it's about to be shown
              if (!this.modal_initialized) {
                const modalElement = document.getElementById('playbook-copy-modal')
                if (modalElement) {
                  this.copy_modal = new bootstrap.Modal(modalElement)
                  
                  // Add event listener for when modal is hidden (only once)
                  modalElement.addEventListener('hidden.bs.modal', () => {
                    this.$emit('update:show', false)
                  })
                  
                  this.modal_initialized = true;
                } else {
                  console.error('Modal element not found!');
                }
              }
              this.showModal()
            })
          } else {
            this.hideModal()
          }
        },
        immediate: true
      },
      originalPlaybook: {
        handler(newPlaybook) {
          if (newPlaybook) {
            this.local_playbook.name = newPlaybook.name + ' (Copy)';
            this.local_playbook.description = newPlaybook.description || '';
            this.local_playbook.date = newPlaybook.start_date || null;
          }
        },
        immediate: true
      }
    },
    methods: {
      showModal() {
        if (this.copy_modal) {
          this.copy_modal.show()
        }
      },
      hideModal() {
        if (this.copy_modal) {
          this.copy_modal.hide()
        }
      },
      closeModal() {
        this.$emit('update:show', false)
      },
      async copyPlaybook() {
        if (!this.local_playbook.name || !this.local_playbook.date || !this.originalPlaybook) {
          return;
        }
        
        // Clear previous errors
        this.errors = {
          name: '',
          description: '',
          date: '',
        };

        // Emit event to show loading screen
        this.$emit('play-creating', { 
          generating: true
        });
        
        try {
          const response = await axios.post(`/api/playbooks/${this.client.url}/copy/${this.originalPlaybook.url}`, {
            name: this.local_playbook.name,
            description: this.local_playbook.description,
            date: this.local_playbook.date,
            date_type: this.date_type,
          });
          
          const playbookId = response.data.playbook.id;
          
          // Emit event with playbook ID to show loading screen
          this.$emit('play-created', {
            playbookId,
            playbookUrl: response.data.playbook.url,
            totalAssets: response.data.playbook.assets?.length || 0,
          });
          
          // Close the modal AFTER emitting the event
          if (this.copy_modal) {
            this.copy_modal.hide();
          }
          
        } catch (error) {
          console.error('Error copying playbook:', error);
          
          // Handle validation errors
          if (error.response && error.response.status === 422) {
            const validationErrors = error.response.data.errors;
            this.errors = {
              name: validationErrors.name ? validationErrors.name[0] : '',
              description: validationErrors.description ? validationErrors.description[0] : '',
              date: validationErrors.date ? validationErrors.date[0] : '',
            };
          } else {
            // Show general error message
            Swal.fire({
              title: 'Error',
              text: 'Failed to copy playbook. Please try again.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
          
          // Emit event to hide loading screen
          this.$emit('play-creating', { 
            generating: false
          });
        }
      }
    }
  });
  </script>
